import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import classNames from 'classnames';
import React, { useEffect } from 'react';
import { generateTrackingId, TrackImpression, Tracker, EventTrackingSession } from '@udemy/event-tracking';
import { useI18n } from '@udemy/i18n';
import { Icon } from '@udemy/icons';
import { ExpressiveIcon } from '@udemy/icons-expressive';
import { SpotIllustration } from '@udemy/illustrations';
import { ExperimentationProvider, useFeatures } from '@udemy/react-experimentation-provider';
import { ToasterStore as toasterStore } from '@udemy/react-messaging-components';
import { expiringLocalStorageWithKeyTTLs } from '@udemy/shared-utils';
import { useUDData } from '@udemy/ud-data';
import { CourseFinderEntryPointDismissed, CourseFinderEntryPointPresented, CourseFinderEntryPointSelected, CourseFinderUIRegionOptions } from '../../../events/guidance-entry-point-events';
import { getRefIDFromLocalStorage, setRefIDToLocalStorage } from '../../../events/storage';
import styles from "./entry-point.module.css";
const FEATURE_NAME = 'course_guidance_feature';
const ONE_WEEK_IN_MS = 7 * 24 * 60 * 60 * 1000;
export const EntryPoint = ({
  showEntryPoint,
  mock
}) => {
  var _featureVariantAssign;
  const {
    gettext
  } = useI18n();
  const {
    me
  } = useUDData();
  const isLoggedIn = me.is_authenticated;
  const toastIdRef = React.useRef(undefined);
  let showEntryPointV3 = false;
  const {
    featureVariantAssignments,
    isLoading: isExpLoading
  } = useFeatures([FEATURE_NAME]);
  showEntryPointV3 = showEntryPoint || ((_featureVariantAssign = featureVariantAssignments.get(FEATURE_NAME)) === null || _featureVariantAssign === void 0 || (_featureVariantAssign = _featureVariantAssign.configuration) === null || _featureVariantAssign === void 0 ? void 0 : _featureVariantAssign.show_entry_point_v3);
  const cacheKey = 'course-guidance-pop-up';
  const storage = expiringLocalStorageWithKeyTTLs('org-admin-home-page', 'org-admin-home-page', ONE_WEEK_IN_MS);
  const isNewSession = () => {
    return !storage.get(cacheKey) || storage.get(cacheKey) != EventTrackingSession.getEventTrackingSessionId();
  };
  const updateSessionData = () => {
    storage.set(cacheKey, EventTrackingSession.getEventTrackingSessionId());
  };
  const getUIRegion = isLoggedIn => {
    const path = window.location.pathname;
    if (path && path === '/' && isLoggedIn) {
      return CourseFinderUIRegionOptions.LIHP_TOAST;
    } else if (path && path.includes('course') && path.includes('search')) {
      return CourseFinderUIRegionOptions.SEARCH_RESULTS_TOAST;
    } else if (path && path.startsWith('/course')) {
      return CourseFinderUIRegionOptions.CLP_TOAST;
    } else if (path && path.includes('topic')) {
      return CourseFinderUIRegionOptions.TOPIC_TOAST;
    } else {
      return CourseFinderUIRegionOptions.LOHP_TOAST;
    }
  };
  const trackCourseFinderEntryPointPresented = () => {
    const trackingId = generateTrackingId();
    setRefIDToLocalStorage('course_finder_tracking_id', trackingId);
    Tracker.publishEvent(new CourseFinderEntryPointPresented({
      uiRegion: getUIRegion(isLoggedIn),
      flowTrackingId: trackingId
    }));
  };
  const trackCourseFinderClicked = isLoggedIn => {
    const trackingId = getRefIDFromLocalStorage('course_finder_tracking_id');
    Tracker.publishEvent(new CourseFinderEntryPointSelected({
      uiRegion: getUIRegion(isLoggedIn),
      flowTrackingId: trackingId
    }));
  };
  const trackCourseFinderEntryPointDismissed = isLoggedIn => {
    const trackingId = getRefIDFromLocalStorage('course_finder_tracking_id');
    Tracker.publishEvent(new CourseFinderEntryPointDismissed({
      uiRegion: getUIRegion(isLoggedIn),
      flowTrackingId: trackingId
    }));
  };
  const goToCourseDiscoveryLanding = category => {
    trackCourseFinderClicked(isLoggedIn);
    if (category) {
      const urlParams = new URLSearchParams();
      urlParams.set('category', category);
      window.location.href = `/course-discovery-landing/?${urlParams.toString()}`;
    } else {
      window.location.href = `/course-discovery-landing/`;
    }
  };
  const createContent = () => {
    return /*#__PURE__*/React.createElement(TrackImpression, {
      trackFunc: trackCourseFinderEntryPointPresented
    }, /*#__PURE__*/React.createElement("div", {
      className: classNames(styles['container'])
    }, /*#__PURE__*/React.createElement("div", {
      className: classNames(styles['header'])
    }, /*#__PURE__*/React.createElement("div", {
      className: classNames(styles['header-container'])
    }, /*#__PURE__*/React.createElement("div", {
      className: classNames(styles['header-text-container'])
    }, /*#__PURE__*/React.createElement(ExpressiveIcon, {
      name: 'digital-transformation',
      size: "small"
    }), /*#__PURE__*/React.createElement("div", {
      className: classNames(styles['header-text'])
    }, /*#__PURE__*/React.createElement("div", {
      className: classNames('ud-text-md')
    }, gettext('Wondering where to begin?')), /*#__PURE__*/React.createElement("div", {
      className: classNames('ud-heading-md', styles['sub-title'])
    }, gettext('We’ll help you find the right course')))), /*#__PURE__*/React.createElement(Icon, {
      className: classNames(styles['pointer']),
      glyph: "close",
      label: "Close",
      size: "medium",
      color: "inherit",
      onClick: dismiss
    }))), /*#__PURE__*/React.createElement("div", {
      className: classNames(styles['body'])
    }, /*#__PURE__*/React.createElement("div", {
      className: classNames('ud-text-sm', styles['body-title'])
    }, gettext('Choose from trending topics in Japan')), /*#__PURE__*/React.createElement("div", {
      className: classNames(styles['cta-container'])
    }, /*#__PURE__*/React.createElement("div", {
      className: classNames(styles['cta-container-row'])
    }, /*#__PURE__*/React.createElement("div", {
      className: classNames(styles['cta-block']),
      onClick: () => goToCourseDiscoveryLanding('design-3danimation')
    }, /*#__PURE__*/React.createElement("div", {
      className: classNames(styles['cta-icon'])
    }, /*#__PURE__*/React.createElement(SpotIllustration, {
      name: 'design',
      alt: 'design',
      height: 96,
      width: 96,
      onClick: () => goToCourseDiscoveryLanding('design-3danimation')
    })), /*#__PURE__*/React.createElement("div", {
      className: classNames('ud-heading-md', styles['cta-text'])
    }, gettext('Create stunning 3D graphics'))), /*#__PURE__*/React.createElement("div", {
      className: classNames(styles['cta-block']),
      onClick: () => goToCourseDiscoveryLanding('development-programming')
    }, /*#__PURE__*/React.createElement("div", {
      className: classNames(styles['cta-icon'])
    }, /*#__PURE__*/React.createElement(SpotIllustration, {
      name: 'development',
      alt: 'development',
      height: 96,
      width: 96,
      onClick: () => goToCourseDiscoveryLanding('development-programming')
    })), /*#__PURE__*/React.createElement("div", {
      className: classNames('ud-heading-md', styles['cta-text'])
    }, gettext('Start coding with confidence')))), /*#__PURE__*/React.createElement("div", {
      className: classNames(styles['cta-container-row'])
    }, /*#__PURE__*/React.createElement("div", {
      className: classNames(styles['cta-block']),
      onClick: () => goToCourseDiscoveryLanding('teaching-language')
    }, /*#__PURE__*/React.createElement("div", {
      className: classNames(styles['cta-icon'])
    }, /*#__PURE__*/React.createElement(SpotIllustration, {
      name: 'academics',
      alt: 'academics',
      height: 96,
      width: 96,
      onClick: () => goToCourseDiscoveryLanding('teaching-language')
    })), /*#__PURE__*/React.createElement("div", {
      className: classNames('ud-heading-md', styles['cta-text'])
    }, gettext('Speak English with confidence'))), /*#__PURE__*/React.createElement("div", {
      className: classNames(styles['cta-block']),
      onClick: () => goToCourseDiscoveryLanding()
    }, /*#__PURE__*/React.createElement("div", {
      className: classNames(styles['cta-icon'], styles['cta-icon-more'])
    }, /*#__PURE__*/React.createElement(SpotIllustration, {
      name: 'catalog',
      alt: 'catalog',
      height: 96,
      width: 96,
      onClick: () => goToCourseDiscoveryLanding()
    })), /*#__PURE__*/React.createElement("div", {
      className: classNames('ud-heading-md', styles['cta-text'])
    }, gettext('Browse more topics'))))))));
  };
  const dismiss = () => {
    if (toastIdRef.current) {
      updateSessionData();
      toasterStore.dismissToast(toastIdRef.current);
      trackCourseFinderEntryPointDismissed(isLoggedIn);
    }
  };
  useEffect(() => {
    if (!isExpLoading && showEntryPointV3 && !mock && isNewSession()) {
      toastIdRef.current = toasterStore.addToast(createContent());
      return () => {
        dismiss();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpLoading, mock, showEntryPointV3]);
  return mock ? createContent() : /*#__PURE__*/React.createElement(React.Fragment, null);
};
export const EntryPointPopUp = ({
  showEntryPoint,
  mock
}) => {
  if (showEntryPoint !== undefined) {
    return /*#__PURE__*/React.createElement(EntryPoint, {
      showEntryPoint: showEntryPoint
    });
  }
  const queryClient = new QueryClient();
  return /*#__PURE__*/React.createElement(QueryClientProvider, {
    client: queryClient
  }, /*#__PURE__*/React.createElement(ExperimentationProvider, {
    features: [FEATURE_NAME]
  }, /*#__PURE__*/React.createElement(EntryPoint, {
    mock: mock,
    showEntryPoint: showEntryPoint
  })));
};
//# sourceMappingURL=entry-point.react-component.js.map